// Text Case Converter Utility Functions

/**
 * Convert text to sentence case (first letter of each sentence capitalized)
 */
export const toSentenceCase = (text) => {
  if (!text) return '';
  return text.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, (match) => match.toUpperCase());
};

/**
 * Convert text to lower case
 */
export const toLowerCase = (text) => {
  if (!text) return '';
  return text.toLowerCase();
};

/**
 * Convert text to UPPER CASE
 */
export const toUpperCase = (text) => {
  if (!text) return '';
  return text.toUpperCase();
};

/**
 * Convert text to Capitalized Case (each word starts with capital)
 */
export const toCapitalizedCase = (text) => {
  if (!text) return '';
  return text.toLowerCase().replace(/\b\w/g, (match) => match.toUpperCase());
};

/**
 * Convert text to aLtErNaTiNg CaSe
 */
export const toAlternatingCase = (text) => {
  if (!text) return '';
  return text
    .split('')
    .map((char, index) => (index % 2 === 0 ? char.toLowerCase() : char.toUpperCase()))
    .join('');
};

/**
 * Convert text to Title Case (following standard title-casing rules)
 * Minor words (articles, prepositions, conjunctions) stay lowercase unless first/last word
 */
export const toTitleCase = (text) => {
  if (!text) return '';
  const minorWords = new Set([
    'a', 'an', 'the', 'and', 'but', 'or', 'nor', 'for', 'yet', 'so',
    'at', 'by', 'in', 'of', 'on', 'to', 'up', 'as', 'is', 'if'
  ]);
  
  return text.toLowerCase().split(' ').map((word, index, arr) => {
    if (index === 0 || index === arr.length - 1 || !minorWords.has(word)) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    }
    return word;
  }).join(' ');
};

/**
 * Convert text to iNVERSE cASE (swap uppercase to lowercase and vice versa)
 */
export const toInverseCase = (text) => {
  if (!text) return '';
  return text
    .split('')
    .map((char) => {
      if (char === char.toUpperCase()) {
        return char.toLowerCase();
      }
      return char.toUpperCase();
    })
    .join('');
};

/**
 * Count characters in text (with and without spaces)
 */
export const countCharacters = (text) => {
  if (!text) return { withSpaces: 0, withoutSpaces: 0 };
  return {
    withSpaces: text.length,
    withoutSpaces: text.replace(/\s/g, '').length
  };
};

/**
 * Count words in text
 */
export const countWords = (text) => {
  if (!text || !text.trim()) return 0;
  return text.trim().split(/\s+/).length;
};

/**
 * Count sentences in text
 */
export const countSentences = (text) => {
  if (!text || !text.trim()) return 0;
  const sentences = text.match(/[.!?]+/g);
  return sentences ? sentences.length : 0;
};

/**
 * Count lines in text
 */
export const countLines = (text) => {
  if (!text) return 0;
  return text.split('\n').length;
};

/**
 * Download text as a .txt file
 */
export const downloadAsText = (text, filename = 'converted-text.txt') => {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    // Fallback for older browsers
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return true;
    } catch (e) {
      document.body.removeChild(textarea);
      return false;
    }
  }
};

// Case converter options for UI
export const caseOptions = [
  {
    id: 'sentence',
    label: 'Sentence case',
    description: 'Capitalize first letter of sentences',
    fn: toSentenceCase,
    example: 'Hello world. This is a test.'
  },
  {
    id: 'lower',
    label: 'lower case',
    description: 'All letters lowercase',
    fn: toLowerCase,
    example: 'hello world'
  },
  {
    id: 'upper',
    label: 'UPPER CASE',
    description: 'All letters uppercase',
    fn: toUpperCase,
    example: 'HELLO WORLD'
  },
  {
    id: 'capitalized',
    label: 'Capitalized Case',
    description: 'Capitalize first letter of each word',
    fn: toCapitalizedCase,
    example: 'Hello World'
  },
  {
    id: 'alternating',
    label: 'aLtErNaTiNg CaSe',
    description: 'Alternate between lowercase and uppercase',
    fn: toAlternatingCase,
    example: 'hElLo WoRlD'
  },
  {
    id: 'title',
    label: 'Title Case',
    description: 'Title formatting with minor words lowercase',
    fn: toTitleCase,
    example: 'The Quick Brown Fox'
  },
  {
    id: 'inverse',
    label: 'iNVERSE cASE',
    description: 'Swap uppercase and lowercase letters',
    fn: toInverseCase,
    example: 'hELLO wORLD'
  }
];
